import { p as proxyCustomElement, H, c as createEvent, h, a as Host } from './index.js';

function noop() { }

function getSymbols(object) {
    return Object.getOwnPropertySymbols(object).filter(symbol => Object.prototype.propertyIsEnumerable.call(object, symbol));
}

function getTag(value) {
    if (value == null) {
        return value === undefined ? '[object Undefined]' : '[object Null]';
    }
    return Object.prototype.toString.call(value);
}

const regexpTag = '[object RegExp]';
const stringTag = '[object String]';
const numberTag = '[object Number]';
const booleanTag = '[object Boolean]';
const argumentsTag = '[object Arguments]';
const symbolTag = '[object Symbol]';
const dateTag = '[object Date]';
const mapTag = '[object Map]';
const setTag = '[object Set]';
const arrayTag = '[object Array]';
const functionTag = '[object Function]';
const arrayBufferTag = '[object ArrayBuffer]';
const objectTag = '[object Object]';
const errorTag = '[object Error]';
const dataViewTag = '[object DataView]';
const uint8ArrayTag = '[object Uint8Array]';
const uint8ClampedArrayTag = '[object Uint8ClampedArray]';
const uint16ArrayTag = '[object Uint16Array]';
const uint32ArrayTag = '[object Uint32Array]';
const bigUint64ArrayTag = '[object BigUint64Array]';
const int8ArrayTag = '[object Int8Array]';
const int16ArrayTag = '[object Int16Array]';
const int32ArrayTag = '[object Int32Array]';
const bigInt64ArrayTag = '[object BigInt64Array]';
const float32ArrayTag = '[object Float32Array]';
const float64ArrayTag = '[object Float64Array]';

function isPlainObject(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    const hasObjectPrototype = proto === null ||
        proto === Object.prototype ||
        Object.getPrototypeOf(proto) === null;
    if (!hasObjectPrototype) {
        return false;
    }
    return Object.prototype.toString.call(value) === '[object Object]';
}

function eq(value, other) {
    return value === other || (Number.isNaN(value) && Number.isNaN(other));
}

function isEqualWith(a, b, areValuesEqual) {
    return isEqualWithImpl(a, b, undefined, undefined, undefined, undefined, areValuesEqual);
}
function isEqualWithImpl(a, b, property, aParent, bParent, stack, areValuesEqual) {
    const result = areValuesEqual(a, b, property, aParent, bParent, stack);
    if (result !== undefined) {
        return result;
    }
    if (typeof a === typeof b) {
        switch (typeof a) {
            case 'bigint':
            case 'string':
            case 'boolean':
            case 'symbol':
            case 'undefined': {
                return a === b;
            }
            case 'number': {
                return a === b || Object.is(a, b);
            }
            case 'function': {
                return a === b;
            }
            case 'object': {
                return areObjectsEqual(a, b, stack, areValuesEqual);
            }
        }
    }
    return areObjectsEqual(a, b, stack, areValuesEqual);
}
function areObjectsEqual(a, b, stack, areValuesEqual) {
    if (Object.is(a, b)) {
        return true;
    }
    let aTag = getTag(a);
    let bTag = getTag(b);
    if (aTag === argumentsTag) {
        aTag = objectTag;
    }
    if (bTag === argumentsTag) {
        bTag = objectTag;
    }
    if (aTag !== bTag) {
        return false;
    }
    switch (aTag) {
        case stringTag:
            return a.toString() === b.toString();
        case numberTag: {
            const x = a.valueOf();
            const y = b.valueOf();
            return eq(x, y);
        }
        case booleanTag:
        case dateTag:
        case symbolTag:
            return Object.is(a.valueOf(), b.valueOf());
        case regexpTag: {
            return a.source === b.source && a.flags === b.flags;
        }
        case functionTag: {
            return a === b;
        }
    }
    stack = stack ?? new Map();
    const aStack = stack.get(a);
    const bStack = stack.get(b);
    if (aStack != null && bStack != null) {
        return aStack === b;
    }
    stack.set(a, b);
    stack.set(b, a);
    try {
        switch (aTag) {
            case mapTag: {
                if (a.size !== b.size) {
                    return false;
                }
                for (const [key, value] of a.entries()) {
                    if (!b.has(key) || !isEqualWithImpl(value, b.get(key), key, a, b, stack, areValuesEqual)) {
                        return false;
                    }
                }
                return true;
            }
            case setTag: {
                if (a.size !== b.size) {
                    return false;
                }
                const aValues = Array.from(a.values());
                const bValues = Array.from(b.values());
                for (let i = 0; i < aValues.length; i++) {
                    const aValue = aValues[i];
                    const index = bValues.findIndex(bValue => {
                        return isEqualWithImpl(aValue, bValue, undefined, a, b, stack, areValuesEqual);
                    });
                    if (index === -1) {
                        return false;
                    }
                    bValues.splice(index, 1);
                }
                return true;
            }
            case arrayTag:
            case uint8ArrayTag:
            case uint8ClampedArrayTag:
            case uint16ArrayTag:
            case uint32ArrayTag:
            case bigUint64ArrayTag:
            case int8ArrayTag:
            case int16ArrayTag:
            case int32ArrayTag:
            case bigInt64ArrayTag:
            case float32ArrayTag:
            case float64ArrayTag: {
                if (typeof Buffer !== 'undefined' && Buffer.isBuffer(a) !== Buffer.isBuffer(b)) {
                    return false;
                }
                if (a.length !== b.length) {
                    return false;
                }
                for (let i = 0; i < a.length; i++) {
                    if (!isEqualWithImpl(a[i], b[i], i, a, b, stack, areValuesEqual)) {
                        return false;
                    }
                }
                return true;
            }
            case arrayBufferTag: {
                if (a.byteLength !== b.byteLength) {
                    return false;
                }
                return areObjectsEqual(new Uint8Array(a), new Uint8Array(b), stack, areValuesEqual);
            }
            case dataViewTag: {
                if (a.byteLength !== b.byteLength || a.byteOffset !== b.byteOffset) {
                    return false;
                }
                return areObjectsEqual(new Uint8Array(a), new Uint8Array(b), stack, areValuesEqual);
            }
            case errorTag: {
                return a.name === b.name && a.message === b.message;
            }
            case objectTag: {
                const areEqualInstances = areObjectsEqual(a.constructor, b.constructor, stack, areValuesEqual) ||
                    (isPlainObject(a) && isPlainObject(b));
                if (!areEqualInstances) {
                    return false;
                }
                const aKeys = [...Object.keys(a), ...getSymbols(a)];
                const bKeys = [...Object.keys(b), ...getSymbols(b)];
                if (aKeys.length !== bKeys.length) {
                    return false;
                }
                for (let i = 0; i < aKeys.length; i++) {
                    const propKey = aKeys[i];
                    const aProp = a[propKey];
                    if (!Object.hasOwn(b, propKey)) {
                        return false;
                    }
                    const bProp = b[propKey];
                    if (!isEqualWithImpl(aProp, bProp, propKey, a, b, stack, areValuesEqual)) {
                        return false;
                    }
                }
                return true;
            }
            default: {
                return false;
            }
        }
    }
    finally {
        stack.delete(a);
        stack.delete(b);
    }
}

function isEqual(a, b) {
    return isEqualWith(a, b, noop);
}

const JW_CDN_BASE_URL = '//ssl.p.jwpcdn.com/player/v/';
function createScriptElement(src) {
    const el = document.createElement('script');
    el.type = 'text/javascript';
    el.src = src;
    el.async = true;
    return el;
}
function addScriptElement(src) {
    return new Promise((resolve, reject) => {
        // Prevent requesting the same source twice
        let el = document.querySelector(`[src="${src}"]`);
        if (el) {
            // Element is still in document and has already been loaded before
            resolve(el);
            return;
        }
        // Create new element and load script
        el = createScriptElement(src);
        el.addEventListener('load', () => {
            return resolve(el);
        });
        el.addEventListener('error', () => {
            // Clean up failed script
            el.remove();
            return reject(new Error('Could not load JW Player.'));
        });
        // Append element (player js code) to body
        document.body.appendChild(el);
    });
}
/**
 * Completely clean up all jwplayer code
 *
 * We need to do this because we're able to load different versions of jwplayer, and having leftover
 * code from other versions will cause conflicts.
 */
function cleanupJwPlayer(version) {
    // Target JW8 style nodes
    for (const node of document.querySelectorAll('style[data-jwplayer-id]')) {
        node.remove();
    }
    // Target style nodes that start with `.jwplayer` or `.jw-`
    for (const node of document.querySelectorAll('head style')) {
        if (node.textContent?.startsWith('.jw-') || node.textContent?.startsWith('.jwplayer')) {
            node.remove();
        }
    }
    // Target scripts by version
    if (typeof version !== 'undefined') {
        for (const node of document.querySelectorAll(`script[src*="${version}"]`)) {
            node.remove();
        }
    }
    for (const node of document.querySelectorAll(`script[id^="__jw-"]`)) {
        node.remove();
    }
    const w = window;
    if ('jwplayer' in w && typeof w.jwplayer === 'function') {
        if ('webpackJsonpjwplayer' in w) {
            w.webpackJsonpjwplayer = undefined;
        }
        w.jwplayer = undefined;
    }
}
const getSrcUrlForVersion = (version) => {
    return version.startsWith('http') ? version : `${JW_CDN_BASE_URL}${version}/jwplayer.js`;
};
function getJwPlayerStatic() {
    const w = window;
    if ('jwplayer' in w && typeof w.jwplayer === 'function') {
        return w.jwplayer;
    }
}
async function retry(fn, interval = 10, count = 0) {
    if (count > 100) {
        return Promise.reject('Failed to load in time');
    }
    const res = fn();
    if (res) {
        return Promise.resolve(res);
    }
    return new Promise((resolve) => {
        setTimeout(() => {
            retry(fn, interval, count + 1).then(resolve);
        }, interval);
    });
}
async function injectJwPlayerStatic(version, playerLicenseKey) {
    // We need a source to inject the player code
    if (!version) {
        throw new Error('Missing JW Player version.');
    }
    const existingJwPlayerStatic = getJwPlayerStatic();
    if (existingJwPlayerStatic) {
        if (existingJwPlayerStatic.initializedVersion === version && existingJwPlayerStatic.key === playerLicenseKey) {
            // The existing player is still valid
            return existingJwPlayerStatic;
        }
        // Remove existing player code before initializing a different version,
        // otherwise we'll get conflicting code
        cleanupJwPlayer(existingJwPlayerStatic.initializedVersion);
    }
    // Try to inject the player code
    await addScriptElement(getSrcUrlForVersion(version));
    // Query again
    const jwPlayerStatic = await retry(() => getJwPlayerStatic(), 10);
    // Set license key after injecting new code
    jwPlayerStatic.key = playerLicenseKey;
    // Set version with which we initialized so that we can query it later
    // if player needs to be removed
    jwPlayerStatic.initializedVersion = version;
    return jwPlayerStatic;
}
function isPercentage(dim) {
    return typeof dim === 'string' && dim.indexOf('%') !== -1;
}

function parsePlayerOptions(options) {
    if (!(options && typeof options === 'object')) {
        return;
    }
    // Prevent passing options to player if we don't have `file` or `playlist`.
    // If we do, it's going to cause a player error...
    if (!(('file' in options && options.file) ||
        options.playlist ||
        (Array.isArray(options.playlist) && options.playlist.length))) {
        return;
    }
    // We're wrapping the player component, so make sure we ignore a width percentage
    if (isPercentage(options.width)) {
        // Return modified copy
        return { ...options, width: '100%' };
    }
    return options;
}
const WuiJwPlayerBaseInjectorContainer = /*@__PURE__*/ proxyCustomElement(class WuiJwPlayerBaseInjectorContainer extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.playerInstantiate = createEvent(this, "playerInstantiate", 7);
        this.playerSetup = createEvent(this, "playerSetup", 7);
        this.playerReady = createEvent(this, "playerReady", 7);
        this.playerError = createEvent(this, "playerError", 7);
    }
    // Watch for changes to player options and setup player again
    _playerOptions(newOptions, oldOptions) {
        if (!isEqual(newOptions, oldOptions)) {
            this._clearEvents();
            this._setupPlayer(newOptions);
        }
    }
    _jwPlayerStatic(newJwPlayerStatic, oldJwPlayerStatic) {
        if (newJwPlayerStatic !== oldJwPlayerStatic) {
            this.playerInstance?.remove();
            this.playerInstance = undefined;
        }
    }
    componentDidRender() {
        if (this.playerInstance) {
            return;
        }
        const div = this.div?.querySelector('div');
        if (!(this.jwPlayerStatic && div)) {
            return;
        }
        try {
            // Create a player instance
            this.playerInstance = this.jwPlayerStatic(div);
            this.playerInstantiate.emit(this.playerInstance);
            this._setupPlayer();
        }
        catch (err) {
            this.playerError.emit(err instanceof Error ? err : new Error(String(err)));
        }
    }
    disconnectedCallback() {
        this.playerInstance?.remove();
    }
    _setupPlayer(rawOpts = this.playerOptions) {
        if (!this.playerInstance) {
            return;
        }
        const parsedOpts = parsePlayerOptions(rawOpts);
        if (!parsedOpts) {
            return;
        }
        this.playerInstance.setup(parsedOpts);
        this.playerSetup.emit(this.playerInstance);
        this._attachEvents();
    }
    _clearEvents() {
        if (!this.playerInstance) {
            return;
        }
        this.playerInstance.off('setupError');
        this.playerInstance.off('ready');
    }
    _attachEvents() {
        if (!this.playerInstance) {
            return;
        }
        this.playerInstance.once('setupError', (err) => {
            this.playerError.emit(new Error(err.message));
        });
        this.playerInstance.once('ready', () => {
            this.playerReady.emit(this.playerInstance);
        });
    }
    render() {
        return (h(Host, { key: '0c299d3f5cd005ae0e18001206d54d67bea875a9', style: { flex: '1' } }, h("div", { key: 'c93f2504755aa6985a6633286ded129aa6980cad' })));
    }
    get div() { return this; }
    static get watchers() { return {
        "playerOptions": ["_playerOptions"],
        "jwPlayerStatic": ["_jwPlayerStatic"]
    }; }
}, [256, "wui-jw-player-base-injector-container", {
        "playerOptions": [16, "player-options"],
        "jwPlayerStatic": [16, "jw-player-static"]
    }, undefined, {
        "playerOptions": ["_playerOptions"],
        "jwPlayerStatic": ["_jwPlayerStatic"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-jw-player-base-injector-container"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-jw-player-base-injector-container":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiJwPlayerBaseInjectorContainer);
            }
            break;
    } });
}
defineCustomElement();

export { WuiJwPlayerBaseInjectorContainer as W, isPercentage as a, defineCustomElement as d, injectJwPlayerStatic as i };
